import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { API_URL } from "../../../utils/constant";
import SalesCreditNoteForm from "./SalesCreditNoteForm";
import Modal from "../../../components/modal";
import { Pen, Trash, ToggleLeft, ToggleRight, Eye } from "lucide-react";
import { customTableStyles } from "../../../utils/tableStylings";

const SalesCreditNote = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/sales-credit-notes`);
      setCreditNotes(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreditNotes();
  }, []);

  const handleAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (creditNote) => {
    setEditData(creditNote);
    setShowModal(true);
  };

  const handleView = async (creditNote) => {
    try {
      const res = await axios.get(`${API_URL}/sales-credit-notes/${creditNote.id}`);
      setViewData(res.data.data);
      setShowViewModal(true);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to fetch credit note details.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the sales credit note!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/sales-credit-notes/${id}`);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The Sales Credit Note has been deleted.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
      fetchCreditNotes();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete the credit note.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await axios.patch(`${API_URL}/sales-credit-notes/status/${id}`);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Status updated successfully.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
      fetchCreditNotes();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to update status.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
    }
  };

  // DataTable Columns
  const columns = [
    {
      name: "Sales CN No",
      selector: (row) => row.sales_cn_no,
      sortable: true,
    },
    {
      name: "Customer ID",
      selector: (row) => row.customer_id,
      sortable: true,
    },
    {
      name: "Invoice No",
      selector: (row) => row.invoice_no,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.date ? new Date(row.date).toLocaleDateString() : '-',
      sortable: true,
    },
    {
      name: "GST Type",
      selector: (row) => row.gst_type,
    },
    {
      name: "Tax Type",
      selector: (row) => row.tax_type,
    },
    {
      name: "Item Value",
      selector: (row) => row.item_value,
    },
    {
      name: "Discount",
      selector: (row) => row.discount,
    },
    {
      name: "Taxable",
      selector: (row) => row.taxable,
    },
    {
      name: "Tax Amount",
      selector: (row) => row.tax_amount,
    },
    {
      name: "Cess Amount",
      selector: (row) => row.cess_amount,
    },
    {
      name: "Total Amount",
      selector: (row) => row.total_amount,
    },
    {
      name: "Items",
      cell: (row) => (
        <span className="badge bg-info">
          {row.items?.length || 0} items
        </span>
      ),
    },
    {
      name: "Status",
      cell: (row) => (
        <span className={`badge ${row.status ? 'bg-success' : 'bg-secondary'}`}>
          {row.status ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleView(row)}
            title="View Details"
          >
            <Eye size={16} />
          </button>
          <button
            className="btn btn-sm btn-warning"
            onClick={() => handleEdit(row)}
            title="Edit"
          >
            <Pen size={16} />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row.id)}
            title="Delete"
          >
            <Trash size={16} />
          </button>
          <button
            className="btn btn-sm btn-info"
            onClick={() => handleToggleStatus(row.id)}
            title="Toggle Status"
          >
            {row.status ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <>
      <div className="row" style={{ marginTop: "60px" }}>
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="text-primary mb-0">Sales Credit Notes</h3>
              <button className="btn btn-primary" onClick={handleAdd}>
                + Add Credit Note
              </button>
            </div>

            <div className="card-body">
              <DataTable
                columns={columns}
                data={creditNotes}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                customStyles={customTableStyles}
              />
            </div>

            {showModal && (
              <SalesCreditNoteForm
                show={showModal}
                heading={editData ? "Edit Sales Credit Note" : "Add Sales Credit Note"}
                initialData={editData}
                onClose={() => setShowModal(false)}
                onSuccess={fetchCreditNotes}
              />
            )}

            {/* View Details Modal */}
            {showViewModal && viewData && (
              <Modal
                show={showViewModal}
                onClose={() => setShowViewModal(false)}
                heading="Sales Credit Note Details"
                size="lg"
              >
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Sales CN No:</strong> {viewData.sales_cn_no || '-'}</p>
                    <p><strong>Customer ID:</strong> {viewData.customer_id}</p>
                    <p><strong>Invoice No:</strong> {viewData.invoice_no || '-'}</p>
                    <p><strong>Date:</strong> {viewData.date ? new Date(viewData.date).toLocaleDateString() : '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>GST Type:</strong> {viewData.gst_type || '-'}</p>
                    <p><strong>Tax Type:</strong> {viewData.tax_type || '-'}</p>
                    <p><strong>Status:</strong> <span className={`badge ${viewData.status ? 'bg-success' : 'bg-secondary'}`}>{viewData.status ? 'Active' : 'Inactive'}</span></p>
                  </div>
                </div>
                
                <h6 className="mt-3 mb-2">Items</h6>
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                      <th>Tax</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.items?.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.product_id}</td>
                        <td>{item.description || '-'}</td>
                        <td>{item.quantity}</td>
                        <td>₹{item.rate}</td>
                        <td>₹{item.amount}</td>
                        <td>₹{item.tax_amount}</td>
                        <td>₹{item.total_amount}</td>
                      </tr>
                    ))}
                    {!viewData.items?.length && (
                      <tr>
                        <td colSpan="7" className="text-center">No items found</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="row mt-3">
                  <div className="col-md-4">
                    <p><strong>Item Value:</strong> ₹{viewData.item_value}</p>
                  </div>
                  <div className="col-md-4">
                    <p><strong>Tax Amount:</strong> ₹{viewData.tax_amount}</p>
                  </div>
                  <div className="col-md-4">
                    <p><strong>Total Amount:</strong> ₹{viewData.total_amount}</p>
                  </div>
                </div>

                {viewData.remarks && (
                  <div className="mt-3">
                    <p><strong>Remarks:</strong> {viewData.remarks}</p>
                  </div>
                )}

                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn btn-light"
                    onClick={() => setShowViewModal(false)}
                  >
                    Close
                  </button>
                </div>
              </Modal>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesCreditNote;
